import type { DayOfWeek, HabitFrequency } from '../interfaces'

export const DAYS_OF_WEEK: { key: DayOfWeek; label: string; short: string }[] =
  [
    { key: 'mon', label: 'Monday', short: 'Mon' },
    { key: 'tue', label: 'Tuesday', short: 'Tue' },
    { key: 'wed', label: 'Wednesday', short: 'Wed' },
    { key: 'thu', label: 'Thursday', short: 'Thu' },
    { key: 'fri', label: 'Friday', short: 'Fri' },
    { key: 'sat', label: 'Saturday', short: 'Sat' },
    { key: 'sun', label: 'Sunday', short: 'Sun' },
  ]

export function getDayOfWeekKey(date: Date, dayStartTime = '00:00'): DayOfWeek {
  const shifted = new Date(date)
  if (dayStartTime) {
    const [h, m] = dayStartTime.split(':').map(Number)
    shifted.setHours(shifted.getHours() - h, shifted.getMinutes() - m, 0, 0)
  }
  const dayIndex = shifted.getDay()
  const mapping: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  return mapping[dayIndex]
}

export function isScheduledDay(
  date: Date,
  weeklyDays?: DayOfWeek[] | null,
  dayStartTime = '00:00',
): boolean {
  if (!weeklyDays || weeklyDays.length === 0) return true
  const dayKey = getDayOfWeekKey(date, dayStartTime)
  return weeklyDays.includes(dayKey)
}

// Date and period helpers extracted from Habit.tsx
export function getDailyKey(date: Date, dayStartTime = '00:00'): string {
  const [h, m] = dayStartTime.split(':').map(Number)
  const shifted = new Date(date)
  shifted.setHours(shifted.getHours() - h, shifted.getMinutes() - m, 0, 0)
  const y = shifted.getFullYear()
  const mon = String(shifted.getMonth() + 1).padStart(2, '0')
  const d = String(shifted.getDate()).padStart(2, '0')
  return `${y}-${mon}-${d}`
}

export function getISOWeek(date: Date, dayStartTime = '00:00'): number {
  const shifted = new Date(date)
  if (dayStartTime) {
    const [h, m] = dayStartTime.split(':').map(Number)
    shifted.setHours(shifted.getHours() - h, shifted.getMinutes() - m, 0, 0)
  }
  const tmp = new Date(
    Date.UTC(shifted.getFullYear(), shifted.getMonth(), shifted.getDate()),
  )
  const dayNum = tmp.getUTCDay() || 7
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1))
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7)
}

export function getWeeklyKey(date: Date, dayStartTime = '00:00'): string {
  const week = getISOWeek(date, dayStartTime)
  const shifted = new Date(date)
  if (dayStartTime) {
    const [h, m] = dayStartTime.split(':').map(Number)
    shifted.setHours(shifted.getHours() - h, shifted.getMinutes() - m, 0, 0)
  }
  const year =
    week === 1 && shifted.getMonth() === 11
      ? shifted.getFullYear() + 1
      : week >= 52 && shifted.getMonth() === 0
        ? shifted.getFullYear() - 1
        : shifted.getFullYear()
  return `${year}-W${String(week).padStart(2, '0')}`
}

export function getPeriodKey(
  frequencyType: HabitFrequency,
  date: Date,
  dayStartTime = '00:00',
): string {
  if (frequencyType === 'weekly') return getWeeklyKey(date, dayStartTime)
  return getDailyKey(date, dayStartTime)
}

export function isSameCalendarDay(
  a: Date,
  b: Date,
  dayStartTime = '00:00',
): boolean {
  return getDailyKey(a, dayStartTime) === getDailyKey(b, dayStartTime)
}

export function isNextCalendarDay(
  target: Date,
  reference: Date,
  dayStartTime = '00:00',
): boolean {
  const refKey = getDailyKey(reference, dayStartTime)
  const refDate = parseDailyKey(refKey, dayStartTime)
  const tomorrow = new Date(refDate)
  tomorrow.setDate(tomorrow.getDate() + 1)
  return (
    getDailyKey(target, dayStartTime) === getDailyKey(tomorrow, dayStartTime)
  )
}

export function isReadOnlyFutureDate(
  target: Date,
  reference: Date,
  dayStartTime = '00:00',
): boolean {
  const refKey = getDailyKey(reference, dayStartTime)
  const refDate = parseDailyKey(refKey, dayStartTime)
  const tomorrow = new Date(refDate)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const tomorrowKey = getDailyKey(tomorrow, dayStartTime)
  const targetKey = getDailyKey(target, dayStartTime)
  return targetKey > tomorrowKey
}

export function parseDailyKey(key: string, dayStartTime = '00:00'): Date {
  const [y, m, d] = key.split('-').map(Number)
  const [h, min] = dayStartTime.split(':').map(Number)
  return new Date(y, m - 1, d, h, min)
}

export function getHabitDay(date: Date, dayStartTime = '00:00'): Date {
  return parseDailyKey(getDailyKey(date, dayStartTime), dayStartTime)
}

export function parseWeeklyKey(key: string, dayStartTime = '00:00'): Date {
  const [yearStr, weekStr] = key.split('-W')
  const year = Number(yearStr)
  const week = Number(weekStr)
  const jan4 = new Date(year, 0, 4)
  const dayOfWeek = jan4.getDay() || 7
  const monday = new Date(jan4)
  monday.setDate(jan4.getDate() - (dayOfWeek - 1) + (week - 1) * 7)
  const [h, min] = dayStartTime.split(':').map(Number)
  monday.setHours(h, min, 0, 0)
  return monday
}

export function computeStreaks(
  keys: string[],
  frequencyType: HabitFrequency,
  frequencyTarget = 1,
  skippedKeys: string[] = [],
  dayStartTime = '00:00',
  weeklyDays?: DayOfWeek[] | null,
): { current: number; best: number } {
  const MS_PER_DAY = 86_400_000

  if (frequencyType === 'x_per_week') {
    if (keys.length === 0) return { current: 0, best: 0 }
    const weekMap = new Map<string, number>()
    for (const key of keys) {
      const wk = getWeeklyKey(parseDailyKey(key, dayStartTime), dayStartTime)
      weekMap.set(wk, (weekMap.get(wk) ?? 0) + 1)
    }
    const qualifyingWeeks = [...weekMap.entries()]
      .filter(([, count]) => count >= frequencyTarget)
      .map(([week]) => week)
      .sort()

    if (qualifyingWeeks.length === 0) return { current: 0, best: 0 }

    let best = 1
    let run = 1
    for (let i = 1; i < qualifyingWeeks.length; i++) {
      const diff =
        (parseWeeklyKey(qualifyingWeeks[i], dayStartTime).getTime() -
          parseWeeklyKey(qualifyingWeeks[i - 1], dayStartTime).getTime()) /
        (MS_PER_DAY * 7)
      if (Math.round(diff) === 1) {
        run++
        if (run > best) best = run
      } else {
        run = 1
      }
    }

    const todayWeek = getWeeklyKey(new Date(), dayStartTime)
    const weekSet = new Set(qualifyingWeeks)
    const prevWeekKey = (k: string): string => {
      const d = parseWeeklyKey(k, dayStartTime)
      d.setDate(d.getDate() - 7)
      return getWeeklyKey(d, dayStartTime)
    }

    let current = 0
    let cursor = todayWeek
    if (!weekSet.has(cursor)) {
      cursor = prevWeekKey(cursor)
      if (!weekSet.has(cursor)) return { current: 0, best }
    }
    while (weekSet.has(cursor)) {
      current++
      cursor = prevWeekKey(cursor)
    }

    return { current, best }
  }

  if (keys.length === 0 && skippedKeys.length === 0)
    return { current: 0, best: 0 }

  const completedSet = new Set(keys)
  const mergedSorted = [...new Set([...keys, ...skippedKeys])].sort()
  const mergedSet = new Set(mergedSorted)

  const hasSpecificDays =
    frequencyType === 'weekly_days' && weeklyDays && weeklyDays.length > 0

  const prevKey = (key: string): string => {
    if (frequencyType === 'weekly') {
      const d = parseWeeklyKey(key, dayStartTime)
      d.setDate(d.getDate() - 7)
      return getWeeklyKey(d, dayStartTime)
    }
    const d = parseDailyKey(key, dayStartTime)
    if (hasSpecificDays) {
      d.setDate(d.getDate() - 1)
      while (!isScheduledDay(d, weeklyDays, dayStartTime)) {
        d.setDate(d.getDate() - 1)
      }
      return getDailyKey(d, dayStartTime)
    }
    d.setDate(d.getDate() - 1)
    return getDailyKey(d, dayStartTime)
  }

  const nextKey = (key: string): string => {
    if (frequencyType === 'weekly') {
      const d = parseWeeklyKey(key, dayStartTime)
      d.setDate(d.getDate() + 7)
      return getWeeklyKey(d, dayStartTime)
    }
    const d = parseDailyKey(key, dayStartTime)
    if (hasSpecificDays) {
      d.setDate(d.getDate() + 1)
      while (!isScheduledDay(d, weeklyDays, dayStartTime)) {
        d.setDate(d.getDate() + 1)
      }
      return getDailyKey(d, dayStartTime)
    }
    d.setDate(d.getDate() + 1)
    return getDailyKey(d, dayStartTime)
  }

  const datesAreConsecutive = (a: string, b: string): boolean => {
    if (hasSpecificDays) {
      return nextKey(a) === b
    }
    if (frequencyType === 'daily') {
      const diff =
        (parseDailyKey(b, dayStartTime).getTime() -
          parseDailyKey(a, dayStartTime).getTime()) /
        MS_PER_DAY
      return diff === 1
    }
    const diff =
      (parseWeeklyKey(b, dayStartTime).getTime() -
        parseWeeklyKey(a, dayStartTime).getTime()) /
      (MS_PER_DAY * 7)
    return Math.round(diff) === 1
  }

  // Filter mergedSorted to scheduled days if weekly_days
  const activeMergedSorted = hasSpecificDays
    ? mergedSorted.filter((k) =>
        isScheduledDay(
          parseDailyKey(k, dayStartTime),
          weeklyDays,
          dayStartTime,
        ),
      )
    : mergedSorted

  let best = 0
  let runCompleted =
    activeMergedSorted.length > 0 && completedSet.has(activeMergedSorted[0])
      ? 1
      : 0
  if (runCompleted > best) best = runCompleted
  for (let i = 1; i < activeMergedSorted.length; i++) {
    if (datesAreConsecutive(activeMergedSorted[i - 1], activeMergedSorted[i])) {
      if (completedSet.has(activeMergedSorted[i])) {
        runCompleted++
        if (runCompleted > best) best = runCompleted
      }
    } else {
      runCompleted = completedSet.has(activeMergedSorted[i]) ? 1 : 0
      if (runCompleted > best) best = runCompleted
    }
  }

  const today = new Date()
  let todayKey =
    frequencyType === 'daily' || frequencyType === 'weekly_days'
      ? getDailyKey(today, dayStartTime)
      : getWeeklyKey(today, dayStartTime)

  if (hasSpecificDays && !isScheduledDay(today, weeklyDays, dayStartTime)) {
    // If today is not a scheduled day, start checking from the most recent past scheduled day
    const past = new Date(today)
    past.setDate(past.getDate() - 1)
    while (!isScheduledDay(past, weeklyDays, dayStartTime)) {
      past.setDate(past.getDate() - 1)
    }
    todayKey = getDailyKey(past, dayStartTime)
  }

  let current = 0
  let cursor = todayKey
  if (!mergedSet.has(cursor)) {
    cursor = prevKey(cursor)
    if (!mergedSet.has(cursor)) return { current, best }
  }
  while (mergedSet.has(cursor)) {
    if (completedSet.has(cursor)) current++
    cursor = prevKey(cursor)
  }

  return { current, best }
}

export function getWeeklyCompletions(
  periodKeys: string[],
  selectedWeekKey: string,
  dayStartTime: string,
): number {
  return periodKeys.filter(
    (periodKey) =>
      getWeeklyKey(parseDailyKey(periodKey, dayStartTime), dayStartTime) ===
      selectedWeekKey,
  ).length
}

/**
 * Returns the number of days remaining in the ISO week (ending Sunday).
 * If `includeToday` is true (e.g. today is not yet completed), includes today:
 * Mon = 7, Tue = 6, Wed = 5, Thu = 4, Fri = 3, Sat = 2, Sun = 1.
 * If `includeToday` is false (e.g. today is already completed), counts remaining days after today:
 * Mon = 6, Tue = 5, Wed = 4, Thu = 3, Fri = 2, Sat = 1, Sun = 0.
 */
export function getWeeklyDaysLeft(
  date: Date,
  dayStartTime = '00:00',
  includeToday = true,
): number {
  const shifted = new Date(date)
  if (dayStartTime) {
    const [h, m] = dayStartTime.split(':').map(Number)
    shifted.setHours(shifted.getHours() - h, shifted.getMinutes() - m, 0, 0)
  }
  const dayIndex = shifted.getDay()
  const dayNumber = dayIndex === 0 ? 7 : dayIndex
  const remaining = 7 - dayNumber + 1
  return includeToday ? remaining : Math.max(0, remaining - 1)
}
